import React, { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import ProjectSidebar from "./ProjectSidebar";
import ProjectStatsHeader from "./ProjectStatsHeader";
import {
  fetchProjectByKey,
  fetchProjectMembers,
  fetchComponents,
  fetchVersions,
  fetchResolutions,
} from "../../../../core/data/redux/projects/thunks";
import {
  selectCurrentProject,
  selectCurrentProjectLoading,
  selectActiveCompany,
} from "../../../../core/data/redux/projects/selectors";
import {
  selectCompanyOptions,
} from "../../../../core/data/redux/leads-queue/selectors";
import { fetchCompanies } from "../../../../core/data/redux/leads-queue/thunks";
import { setActiveCompany } from "../../../../core/data/redux/projects/slice";
import { useProjectSocket } from "../../hooks/useProjectSocket";
import type { AppDispatch } from "../../../../core/data/redux/store";
import Footer from "../../components/Footer";
import ProjectsCommonHeader from "../../components/ProjectsCommonHeader";

const ProjectShell: React.FC = () => {
  const { projectKey } = useParams<{ projectKey: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector(selectCurrentProject);
  const loading = useSelector(selectCurrentProjectLoading);
  const activeCompany = useSelector(selectActiveCompany);
  const companyOptions = useSelector(selectCompanyOptions);

  useProjectSocket(project?.id);

  useEffect(() => {
    if (companyOptions.length === 0) {
      dispatch(fetchCompanies());
    } else if (!activeCompany?.company_id && companyOptions.length > 0) {
      const first = companyOptions[0];
      const [companyId, companyType] = first.value.split("-");
      const companyName = first.label.split("-")[0].trim();
      dispatch(
        setActiveCompany({
          company_id: companyId.trim(),
          company_name: companyName,
          company_type: companyType.trim(),
        })
      );
    }
  }, [dispatch, companyOptions, activeCompany?.company_id]);

  useEffect(() => {
    if (projectKey && activeCompany?.company_id) {
      dispatch(fetchProjectByKey(projectKey));
    }
  }, [dispatch, projectKey, activeCompany?.company_id]);

  useEffect(() => {
    if (project?.id) {
      dispatch(fetchProjectMembers(project.id));
      dispatch(fetchComponents(project.id));
      dispatch(fetchVersions(project.id));
      dispatch(fetchResolutions(project.id));
    }
  }, [dispatch, project?.id]);

  return (
    <div className="page-wrapper">
      <div className="content">

        <ProjectsCommonHeader />

        {!activeCompany?.company_id || (loading && !project) ? (
          <div
            className="d-flex justify-content-center align-items-center bg-white"
            style={{ minHeight: 400, borderRadius: 14 }}
          >
            <div className="text-center">
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: "2.5rem", height: "2.5rem" }}
              />
              <p className="mt-3 text-muted" style={{ fontSize: 13.5 }}>
                Loading project...
              </p>
            </div>
          </div>
        ) : (
          <>
            <ProjectStatsHeader project={project} />
            <div className="row">
              <div className="col-lg-3 col-xl-2 col-md-4">
                <ProjectSidebar projectKey={projectKey || ""} />
              </div>
              <div className="col-lg-9 col-xl-10 col-md-8">
                <Outlet />
              </div>
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProjectShell;
